import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { DraftOrder, DraftOrderBlocker } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyCompleteForm, toCompleteBody } from '../api/mappers';
import { useCompleteDraftOrder } from '../hooks/use-draft-order-mutations';
import { useDraftPaymentOptions } from '../hooks/use-draft-payment-options';
import {
  completeSchema,
  type CompleteFormInput,
  type CompleteFormOutput,
} from '../schemas/complete-schema';
import { blockersFromConflict, isStateConflict } from '../schemas/draft-order-rules';

import { BlockerList } from './blocker-list';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['manualPaymentMethodId', 'reference', 'note']);

export interface CompleteDialogProps {
  storeId: string;
  draft: DraftOrder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Turns the draft into an order, either paid already or awaiting a manual payment. A 409 lists
 * `meta.blockers` (something changed since the page loaded); a 422 on the method means it was
 * switched off meanwhile. The methods come from the draft payment options, readable with
 * `orders.write` like the completion itself; when they cannot be loaded the dialog says so
 * instead of showing an empty select.
 */
export function CompleteDialog({ storeId, draft, open, onOpenChange }: CompleteDialogProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const format = useFormat();
  const message = useErrorMessage();
  const complete = useCompleteDraftOrder(storeId);
  const [blockers, setBlockers] = useState<DraftOrderBlocker[] | null>(null);
  const form = useForm<CompleteFormInput, unknown, CompleteFormOutput>({
    resolver: useI18nResolver(completeSchema),
    defaultValues: emptyCompleteForm(),
  });
  const type = form.watch('type');
  const methods = useDraftPaymentOptions(storeId, type === 'manual');
  const active = methods.data ?? [];

  const close = () => {
    onOpenChange(false);
    complete.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    setBlockers(null);
    try {
      await complete.mutateAsync({ id: draft.id, body: toCompleteBody(values) });
      close();
    } catch (error) {
      const listed = blockersFromConflict(error);
      if (listed && listed.length > 0) {
        setBlockers(listed);
        return;
      }
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:complete.stateChanged') : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('draftOrders:complete.title', { name: draft.name })}</DialogTitle>
          <DialogDescription>{t('draftOrders:complete.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {blockers ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>
                  <p>{t('draftOrders:complete.blocked')}</p>
                  <BlockerList blockers={blockers} />
                </AlertDescription>
              </Alert>
            ) : null}
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <p className="text-sm font-medium tabular-nums">
              {t('draftOrders:complete.total', { amount: format.money(draft.total) })}
            </p>
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('draftOrders:complete.type')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        form.clearErrors();
                      }}
                      className="gap-2"
                    >
                      {(['paid', 'manual'] as const).map((option) => (
                        <div key={option} className="flex items-start gap-3 rounded-md border p-3">
                          <RadioGroupItem
                            id={`complete-${option}`}
                            value={option}
                            className="mt-0.5"
                          />
                          <Label
                            htmlFor={`complete-${option}`}
                            className="flex-col items-start gap-0.5 font-normal"
                          >
                            <span className="font-medium">
                              {t(`draftOrders:complete.${option}`)}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {t(`draftOrders:complete.${option}Hint`)}
                            </span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {type === 'manual' ? (
              <FormField
                control={form.control}
                name="manualPaymentMethodId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('draftOrders:complete.method')}</FormLabel>
                    {methods.isError ? (
                      <div className="flex flex-wrap items-center gap-2" role="status">
                        <p className="text-muted-foreground text-sm">
                          {t('draftOrders:complete.methodsUnavailable', {
                            reason: message(methods.error),
                          })}
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void methods.refetch()}
                        >
                          {t('common:actions.retry')}
                        </Button>
                      </div>
                    ) : active.length === 0 && methods.isSuccess ? (
                      <p className="text-muted-foreground text-sm">
                        {t('draftOrders:complete.noMethods')}
                      </p>
                    ) : (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={t('draftOrders:complete.methodPlaceholder')}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {active.map((method) => (
                            <SelectItem key={method.id} value={method.id}>
                              {method.fee.amount > 0
                                ? t('draftOrders:complete.fee', {
                                    name: method.name,
                                    fee: format.money(method.fee),
                                  })
                                : method.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <>
                <FormField
                  control={form.control}
                  name="reference"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('draftOrders:complete.reference')}</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="off"
                          placeholder={t('draftOrders:complete.referencePlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="note"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('draftOrders:complete.note')}</FormLabel>
                      <FormControl>
                        <Textarea
                          rows={2}
                          placeholder={t('draftOrders:complete.notePlaceholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={complete.isPending}>
                {complete.isPending
                  ? t('draftOrders:complete.submitting')
                  : t('draftOrders:complete.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
